import { Hint } from '@cotera/client/app/components/app';
import { classNames } from '@cotera/client/app/components/utils';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Button, Text } from '@cotera/client/app/components/ui';
import { startCase } from 'lodash';
import { EraQL, Expression, Relation } from '@cotera/era';
import type * as monaco from 'monaco-editor';
import type { IDisposable } from 'monaco-editor';
import Editor, { Monaco } from '@monaco-editor/react';
import {
  ColumnCompletionProvider,
  FunctionCompletionProvider,
} from './completion-providers';
import { useRef } from 'react';
import { Result } from 'neverthrow';
import { v4 } from 'uuid';
import { DG } from '@cotera/sdk/core';

type Props = {
  height?: number;
  className?: string;
  inline?: boolean;
  templated?: boolean;
  error?: {
    t: string;
    msg: string;
  };
  eraql: string;
  parser: DG.Graph.RelativeParser;
  graph?: DG.BuildExplorer;
  type?: 'expr' | 'rel';
  onChange: (value: string) => void;
  children?: React.ReactNode;
};

export const TemplatedExpressionEditor: React.FC<Props> = ({
  parser,
  onChange,
  error,
  eraql: prompt,
  templated = true,
  inline,
  type = 'expr',
  children,
  height,
  className,
  graph,
}) => {
  const changeHandler = (value: string) => {
    onChange(value);
  };

  return (
    <>
      <MonacoEditor
        className={className}
        children={children}
        templated={templated}
        onChange={changeHandler}
        value={prompt}
        parser={parser}
        graph={graph}
        inline={inline}
        height={height}
        type={type}
        validate={(v) => {
          const res: EraQL.ParseRes<Expression | Relation> =
            type === 'expr' ? parser.parseExpr(v) : parser.parseRel(v);
          return res
            .map(() => true)
            .mapErr((errs) =>
              errs.map((e) => [EraQL.formatParseError(e[0]), e[1]])
            );
        }}
      />
      {templated && !inline && <TypeHints />}
      {prompt.length > 0 && error !== undefined && (
        <div
          className={classNames(
            'p-2 rounded mt-4 text-sm border',
            'bg-destructive/20',
            'text-destructive',
            'border-destructive'
          )}
        >
          {startCase(error.t)}: {error.msg}
        </div>
      )}
    </>
  );
};

export const EraQLDocsButton = () => {
  return (
    <Button
      iconOnly
      text="EraQL Docs"
      tooltip="left"
      small
      compact
      icon="help"
      onClick={() => {
        window.open('/docs/eraql', '_blank');
      }}
    />
  );
};

const TypeHints = () => (
  <div className="flex items-center justify-between pt-4">
    <Badge variant="orange" className="mr-2">
      Template Mode
    </Badge>
    <Text.Caption className="mr-2">
      Expressions must be written within <Hint>{'{{ }}'}</Hint>
    </Text.Caption>
  </div>
);

const LANGUAGE_ID = 'eraql';

class CompletionProviderContainer {
  private instances: WeakMap<WeakKey, string> = new WeakMap();
  private providers: Map<string, IDisposable[]> = new Map();

  register(
    monaco: Monaco,
    languageId: string,
    providers: monaco.languages.CompletionItemProvider[]
  ) {
    let key = this.instances.get(monaco);
    if (key === undefined) {
      key = v4();
      this.instances.set(monaco, key);
    }

    const existing = this.providers.get(key);
    if (existing) {
      existing.forEach((p) => p.dispose());
    }
    this.providers.set(
      key,
      providers.map((p) =>
        monaco.languages.registerCompletionItemProvider(languageId, p)
      )
    );
  }
}

const completions = new CompletionProviderContainer();

function MonacoEditor({
  templated = true,
  children,
  height,
  ...props
}: {
  onChange: (value: string) => void;
  className?: string;
  value: string;
  parser: DG.Graph.RelativeParser;
  graph?: DG.BuildExplorer;
  templated?: boolean;
  inline?: boolean;
  type: 'expr' | 'rel';
  validate: (
    value: string
  ) => Result<
    boolean,
    EraQL.WithRange<{ readonly t: string; readonly msg: string }>[]
  >;
  children?: React.ReactNode;
  height?: number;
}) {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  function handleEditorDidMount(
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) {
    editorRef.current = editor;
    monacoRef.current = monaco;

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK, () => {
      window.dispatchEvent(new CustomEvent('open-command-palette'));
    });
    editor.addCommand(monaco.KeyMod.WinCtrl | monaco.KeyCode.KeyK, () => {
      window.dispatchEvent(new CustomEvent('open-command-palette'));
    });

    // Register custom language
    monaco.languages.register({ id: LANGUAGE_ID });

    monaco.languages.setLanguageConfiguration(LANGUAGE_ID, {
      autoClosingPairs: [
        { open: '{{', close: '}}' },
        { open: '"', close: '"' },
        { open: '(', close: ')' },
        { open: '[', close: ']' },
        { open: "'", close: "'" },
      ],
      surroundingPairs: [
        { open: '{{', close: '}}' },
        { open: '"', close: '"' },
      ],
      // brackets: [['{', '}']],
    });

    monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
      tokenizer: {
        root: [
          // Match an identifier that is followed by '(' indicating a function call.
          // This will mark function calls with the 'function-call' token.
          [/\{\{/, 'expression.start'], // Place this at the top if needed
          [/\}\}/, 'expression.end'], // Place this at the top if needed
          [/[a-zA-Z_]\w*(?=\()/, 'function-call'],
          [/"/, { token: 'string.quote', next: '@string' }],
        ],
        string: [
          // Match any sequence of characters that are not a double quote
          // and assign them the 'string.content' token
          [/[^"]+/, 'string.content'],

          // When encountering another double quote, close the string
          [/"/, { token: 'string.quote', next: '@pop' }],
        ],
      },
    });

    completions.register(monaco, LANGUAGE_ID, [
      ColumnCompletionProvider({
        isTemplated: templated,
        parser: props.parser,
        monacoInstance: monaco,
        graph: props.graph,
      }),
      FunctionCompletionProvider({
        isTemplated: templated,
        parser: props.parser,
        type: props.type,
        monacoInstance: monaco,
      }),
    ]);

    editor.focus();

    monaco.editor.defineTheme('coteraTheme', {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'function-call', foreground: '10b981' },
        { token: 'expression.start', foreground: '0369a1' },
        { token: 'expression.end', foreground: '0369a1' },
        { token: 'string.quote', foreground: '71717a' },
        { token: 'string.content', foreground: 'a1a1aa' },
      ],
      colors: {
        'editor.lineHighlightBackground': '#00000000',
        'editor.lineHighlightBorder': '#00000000',
      },
    });

    monaco.editor.setTheme('coteraTheme');
  }

  const changeHandler = (input: string | undefined) => {
    if (editorRef.current === null || monacoRef.current === null) {
      return;
    }

    const model = editorRef.current.getModel()!;

    const value = input ?? '';
    const parseRes = props.validate(!templated ? value : `\`${value}\``);

    if (parseRes.isErr()) {
      const markers: monaco.editor.IMarkerData[] = [];

      for (const [{ msg }, { range }] of parseRes.error) {
        if (range === null || range === undefined) {
          continue;
        }
        const startPos = model.getPositionAt(range[0]);
        const endPos = model.getPositionAt(range[1]);

        markers.push({
          severity: monacoRef.current.MarkerSeverity.Error,
          startLineNumber: startPos.lineNumber,
          startColumn: startPos.column,
          endLineNumber: endPos.lineNumber,
          endColumn: endPos.column,
          message: msg,
        });
      }

      monacoRef.current.editor.setModelMarkers(model, 'owner', markers);
    } else if (parseRes.isOk()) {
      monacoRef.current.editor.setModelMarkers(model, 'owner', []);
    }

    props.onChange(value);
  };

  const inlineProps = {
    lineNumbers: props.inline ? ('off' as const) : ('on' as const),
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 0,
    glyphMargin: false,
    folding: false,
    overviewRulerLanes: 0,
  };

  let finalHeight;
  if (height !== undefined) {
    finalHeight = `${height}px`;
  } else if (props.inline) {
    finalHeight = '24px';
  } else {
    finalHeight = '300px';
  }

  return (
    <div
      className={classNames(
        'w-full border rounded-md border-divider shadow-xs',
        props.inline ? 'px-1 pb-0.5 pt-1.5' : 'py-4',
        props.className
      )}
    >
      {children}
      <Editor
        value={props.value}
        onChange={changeHandler}
        height={finalHeight}
        defaultLanguage={LANGUAGE_ID}
        onMount={handleEditorDidMount}
        options={{
          automaticLayout: true,
          minimap: { enabled: false },
          scrollbar: {
            horizontalScrollbarSize: 2,
            alwaysConsumeMouseWheel: false,
          },
          ...(props.inline ? inlineProps : {}),
        }}
      />
    </div>
  );
}
